<template>
  <div class="gallery-index">
    <div class="gallery-index__main-content">
      <div class="gallery-filter__header-wrapper js-filter-header-wrapper">
        <GalleryFilterHeader
          :filters="filters"
          :selectedFilters="selectedFilters"
          :totalResults="totalResults"
          :searchTerm="searchTerm"
        />
      </div>
      <div class="gallery-filter__option-categories-wrapper js-filter-header">
        <GalleryFilterOptions
          :filterOptionsCategories="filtersByCategory"
          :selectedFilters="selectedFilters"
          :searchTerm="searchTerm"
        />
      </div>
      <GalleryProjectModal
        v-if="results.length > 0"
        :gallery-project-modal-id="this.galleryProjectModalId"
        :modal-open="galleryProjectModalOpen"
        :modal-data="modal"
        :project-id="selectedProjectIndex"
        :link="results[selectedProjectIndex]"
        :display-name="results[selectedProjectIndex]"
        :tags="results[selectedProjectIndex]"
        :searchTerm="searchTerm"
      />
      <span class="gallery-index__results-grid-container">
        <GalleryResultsGrid
          :results="this.results"
          :error="error"
          :api-call-made="apiCallMade"
          :current-page="Number(page)"
          :gallery-project-modal-id="this.galleryProjectModalId"
          :total-results="totalResults"
          :per-page="24"
          :no-results-message="cmsPage.noResultsMessage"
          :no-results-redirect="cmsPage.noResultsLinkUrl"
          :no-results-redirect-text="cmsPage.noResultsLinkText"
          :no-results-open-chat="false"
          :total-pages="totalPages"
          :loading-data="loadingData"
        />
        <div class="resources-index__loading-container">
          <div v-if="loadingData" class="loading" />
        </div>
      </span>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MicroModal from 'micromodal';
import queryString from 'query-string';

import GalleryResultsGrid from './GalleryResultsGrid';
import GalleryFilterHeader from './GalleryFilterHeader';
import GalleryFilterOptions from './GalleryFilterOptions';
import GalleryProjectModal from './modal/GalleryProjectModal';

import {
  eventBus,
  CLEAR_FILTERS,
  UPDATE_FILTERS,
  UPDATE_SEARCH_TERM,
  UPDATE_SELECTED_GALLERY_PROJECT,
  CLOSE_GALLERY_PROJECT_MODAL,
} from '../shared/event-bus';

// Local non-shifting scroll lock: just toggles overflow on <html> so the page
// behind the modal never moves. Avoids the body-position-fixed trick in
// helpers/disableScroll, which scrolls the page on open/close.
function lockGalleryScroll(lock) {
  const html = document.documentElement;
  if (lock) {
    const scrollbarWidth = window.innerWidth - html.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    html.style.overflow = 'hidden';
  } else {
    html.style.overflow = '';
    document.body.style.paddingRight = '';
  }
}

import {
  adobeAnalyticsVirtualPageLoad,
  adobeCleanString,
  fireAdobeAnalyticsEvent,
} from '../../helpers/adobe-analytics';

// the various string keys that we need to send to Libris SEACH API
const queryKeys = {
  QUERY_SEARCH_TERM: 'searchTerm',
  QUERY_FILTERS: 'filterOptions',
  QUERY_CURRENT_PAGE: 'page',
};

// a mapping of the query strings to the state object properties
const queryToStateMap = {
  [queryKeys.QUERY_SEARCH_TERM]: 'searchTerm',
  [queryKeys.QUERY_FILTERS]: 'selectedFilters',
  [queryKeys.QUERY_CURRENT_PAGE]: 'page',
};

const perPage = 24;

// strip out the string mainpulation for the url into a function
// if you are appending to the a url that already has a query string '?'
// then do one string manipulation
// otherwise to just add
// Taken from Libris Search
const newUrlCreator = appendMode => (stringAcc, obj, val) => {
  if ((val == 'searchTerm' && obj[val] != '') || val == 'page') {
    stringAcc = appendMode
      ? `${stringAcc}&${val}=${obj[val]}`
      : `?${val}=${obj[val]}`;
  } else {
    const valToUse = obj[val];
    stringAcc = valToUse
      ? appendMode
        ? `${stringAcc}&${val}=${valToUse}`
        : `?${val}=${valToUse}`
      : stringAcc;
  }

  return stringAcc;
};

export default {
  name: 'GalleryIndex',
  components: {
    GalleryResultsGrid,
    GalleryFilterHeader,
    GalleryFilterOptions,
    GalleryProjectModal,
  },
  props: ['pageContent', 'filterOptionsCategories', 'modal'],
  data() {
    return {
      searchTerm: '',
      selectedFilters: [],
      totalResults: 0,
      error: null,
      results: [],
      page: 1,
      totalPages: null,
      apiCallMade: true,
      loadingData: false,
      lastPageLoaded: false,
      selectedProjectIndex: 0,
      galleryProjectModalId: 'modal-gallery-project',
      galleryProjectModalOpen: false,
      galleryProjectReady: false,
      debounceTimeout: null,
    };
  },
  computed: {
    cmsPage() {
      if (this.pageContent) {
        return JSON.parse(this.pageContent);
      }

      return {
        title: 'Default title -- expected JSON',
        subheading: '',
        noResultsMessage: 'No Results',
        noResultsLinkText: 'Reset Filters',
        noResultsLinkUrl: 'https://www.3-form.com/gallery',
      };
    },
    filtersByCategory() {
      if (this.filterOptionsCategories) {
        return JSON.parse(this.filterOptionsCategories);
      }

      return {};
    },
    filters() {
      let result = {};

      this.filtersByCategory.forEach(category => {
        if (category.options) {
          category.options.forEach(option => {
            result[option.id] = option.displayName;
          });
        }
      });

      return result;
    },
    optionsSorting() {
      let result = {};

      this.filtersByCategory.forEach((category, catIndex) => {
        category.options.forEach((option, optIndex) => {
          result[option.id] = `${catIndex}__${optIndex}`;
        });
      });

      return result;
    },
    projectIdFromUrl() {
      const matches = window.location.pathname.match(/^\/gallery\/(\d+)/);
      return matches ? Number(matches[1]) : null;
    },
  },
  async mounted() {
    window.onpopstate = async () => {
      await this.handleSearchTerms();
      await this.openDefaultCategories();
      await this.updateFilterInputs();
      await this.setHeaderElements();
      await this.sortSelectedFilters();
      // await this.setGalleryProject();

      // Always request resources last unless something requires results
      await this.requestResources();
    };

    await this.handleSearchTerms();
    await this.openDefaultCategories();
    await this.updateFilterInputs();
    await this.setHeaderElements();
    await this.sortSelectedFilters();

    // Always request resources last unless something requires results
    await this.requestResources();

    // Scrolling does two things:
    //   changes header / options elements from static to fixed
    //   loads the next page
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    /**
     * Vue works with the concept of Props down and Events up.
     * Rather than set up a bunch of event listeners and listening all the way
     * through the component tree it is a pub/sub system that allows us to emit
     * events and have components change based on the events they subscribe to.
     * read more here: https://alligator.io/vuejs/global-event-bus/
     */
    eventBus.$on(CLEAR_FILTERS, () => {
      this.selectedFilters.forEach(selectedFilter => {
        const selector = `.js-resources-filter-option[data-id="${selectedFilter}"] input[type=checkbox]`;
        document.querySelector(selector).checked = false;
      });

      this.selectedFilters = [];
      this.updateUrlFilters();
    });

    eventBus.$on(UPDATE_FILTERS, data => {
      if (data.checked) {
        this.selectedFilters.push(data.option?.id);
        this.sortSelectedFilters();
      } else {
        const index = this.selectedFilters.indexOf(data.option?.id);
        if (index !== -1) this.selectedFilters.splice(index, 1);
      }

      // ANALYTICS
      const clickAction = data.checked ? 'Select' : 'DeSelect';
      const categoryName = data.category?.displayName;
      const optionName = data.option?.displayName;
      const filtersEventString = `Project Gallery|Filter ${categoryName}|${clickAction} ${optionName}`;
      this.sendAdobeToolEvent(filtersEventString);

      if (!data.skipFetch) this.updateUrlFilters();
    });

    eventBus.$on(UPDATE_SEARCH_TERM, data => {
      if (data.length > 1 || data.length === 0) {
        this.searchTerm = data;
        eventBus.clearAllFilters();

        if (!!this.searchTerm.length) {
          this.sendAdobeSearchEvent();
        }
      }
    });

    eventBus.$on(UPDATE_SELECTED_GALLERY_PROJECT, project => {
      const directOpenFromUrl = typeof project === 'number';
      // If a project has been deselected, the projectId needs to be 0
      let projectId = directOpenFromUrl ? project : project?.id || 0;

      this.updateUrl(projectId);
      // A deep link's own page load already named the project, so only an
      // in-page open sends one, after the URL moves so the hit carries it.
      if (!directOpenFromUrl && !!project?.name) {
        this.sendAdobeDetailPageLoadEvent(project);
      }
      this.selectedProjectIndex = projectId;
      this.galleryProjectModalOpen = projectId > 0;

      lockGalleryScroll(this.galleryProjectModalOpen);
      this.setGalleryProject(projectId);
    });

    this.galleryProjectReady = true;
    eventBus.updateSelectedGalleryProject(this.projectIdFromUrl);
  },
  methods: {
    setGalleryProject(projectId) {
      if (projectId === 0 || projectId === null) return;

      const setGalleryProjectIntervalId = setInterval(() => {
        if (!this.galleryProjectReady) return;

        clearInterval(setGalleryProjectIntervalId);

        MicroModal.show('modal-gallery-project', {
          onClose: modal => {
            eventBus.closeGalleryProjectModal();
          },
        });
      }, 100);
    },
    updateUrl(id = 0) {
      const search = window.location.search,
        pathname = id === 0 || !id ? '/gallery' : `/gallery/${id}`;

      history.pushState({ project: id }, `newUrl`, `${pathname}${search}`);
    },
    requestResources() {
      // since we're making a new request, we will be loading new data
      this.loadingData = true;

      const queryParams = location.search,
        separator = queryParams.length > 0 ? '&' : '?',
        pageSlug = this.cmsPage.slug,
        sampleTypeSlug = this.cmsPage.sampleTypeSlug
          ? `/${this.cmsPage.sampleTypeSlug}`
          : '';

      return axios
        .get(`/gallery.json${queryParams}${separator}page=${this.page}`)
        .then(({ data }) => {
          const currentResultIds = [];
          this.results.map(result => currentResultIds.push(result.id));
          data.results.forEach(result => {
            if (!currentResultIds.includes(result.id)) {
              this.results.push(result);
            }
          });

          this.totalPages = Math.ceil(data.totalResults / perPage);
          this.totalResults = data.totalResults;
          this.lastPageLoaded =
            this.page === this.totalPages || this.totalPages === 0;

          this.handleScroll();
          this.loadingData = false;
        });
    },

    // TODO: DRY - These next few methods are taken from LibrisSearch
    handleSearchTerms() {
      const parsed = queryString.parse(location.search);

      // loop over the query params that came from the URL
      for (let [key, value] of Object.entries(parsed)) {
        // first check to see if the key val pair is valid for our app
        if (this.isValidQueryString(value, key)) {
          // set state based on the valid query string
          if (
            key === queryKeys.QUERY_SEARCH_TERM ||
            key === queryKeys.QUERY_CURRENT_PAGE
          ) {
            this[key] = value;
          } else {
            // filterOptions
            const newState = value.split(/[,\|]/).map(val => {
              return Number(val);
            });
            const [firstKey, secondKey] = queryToStateMap[key].split('.');

            if (newState) {
              if (!secondKey) {
                this[firstKey] = newState;
              } else {
                this[firstKey][secondKey] = newState;
              }
            }
          }
        }
      }
    },
    updateFilterInputs() {
      this.selectedFilters.forEach(optionID => {
        let selector = `.js-resources-filter-option[data-id="${optionID}"] input[type=checkbox]`;
        document.querySelector(selector).checked = true;
      });
    },
    isValidQueryString(obj, key) {
      if (!obj) {
        return false;
      }

      // make sure the user passed in a string and that the key that was passed was a valid key
      return (
        typeof obj === 'string' &&
        Object.values(queryKeys).some(val => val == key)
      );
    },
    updateUrlFilters(skipRequest = false) {
      const { searchTerm, filtersByCategory, selectedFilters } = this,
        filterOptions = filtersByCategory.reduce(
          (arrayAccumulator, category, categoryIndex) => {
            const categorySelectedOptions = category.options.filter(option => {
                return (
                  selectedFilters.indexOf(option.id) > -1 &&
                  !arrayAccumulator.some(value =>
                    value.match(new RegExp(`([,|]|^)${option.id}([,|]|$)`))
                  )
                );
              }),
              lastOptionIndex = categorySelectedOptions.length - 1;

            if (lastOptionIndex === -1) return arrayAccumulator;

            const categoryIDs = categorySelectedOptions.reduce(
              (stringAccumulator, option, optionIndex) => {
                stringAccumulator += option.id;

                if (optionIndex < lastOptionIndex) {
                  stringAccumulator += category.inclusive ? '|' : ',';
                }

                return stringAccumulator;
              },
              ''
            );

            arrayAccumulator.push(categoryIDs);

            return arrayAccumulator;
          },
          []
        );

      // create new query params based off of our App state
      let params = {};
      if (searchTerm) params.searchTerm = searchTerm;
      if (filterOptions.length > 0) params.filterOptions = filterOptions;

      const newQueryParams = Object.keys(params).reduce((accumulator, val) => {
        const regex = /\?/;
        const modeAware = newUrlCreator(Boolean(accumulator.match(regex)));
        accumulator = modeAware(accumulator, params, val);
        return accumulator;
      }, '');

      const pathParts = window.location.pathname.match(
          /(\/(landing))?\/(\w+)(\/([\w-]+))?/
        ),
        currentSlug = pathParts[5],
        landingPrefix = pathParts[2];

      let newPath = pathParts[3];

      if (currentSlug && !this.cmsPage.sampleTypeSlug) {
        newPath = newPath + '/' + currentSlug;
      } else if (this.cmsPage.sampleTypeSlug) {
        newPath = newPath + '/' + this.cmsPage.sampleTypeSlug;
      }

      if (landingPrefix) newPath = landingPrefix + '/' + newPath;

      const newUrl = newPath + newQueryParams;

      history.pushState({}, 'newUrl', '/' + newUrl);

      if (!skipRequest) {
        const requestIntervalID = setInterval(() => {
          if (this.loadingData) return;

          clearInterval(requestIntervalID);

          this.resetResults();
          this.requestResources();
        }, 50);
      }
    },
    handleScroll() {
      if (
        !this.loadingData &&
        !this.lastPageLoaded &&
        window.innerHeight + window.pageYOffset >=
          document.body.offsetHeight - 1250
      ) {
        this.page++;
        this.requestResources();
      }
    },
    setHeaderElements() {
      this.header = document.querySelector('.js-filter-header');
      this.headerWrapper = this.header.closest('.js-filter-header-wrapper');
      this.options = document.querySelector('.js-filter-options');
      this.optionsWrapper = this.options.parentNode;
      this.grid = document.querySelector('.js-results-grid');
    },
    resetResults() {
      this.results = [];
      this.totalResults = 0;
      this.page = 1;
    },
    sortSelectedFilters() {
      this.selectedFilters.sort((aOptionID, bOptionID) => {
        const [aCategory, aOption] = this.optionsSorting[aOptionID]
            .split('__')
            .map(numberString => {
              return Number(numberString);
            }),
          [bCategory, bOption] = this.optionsSorting[bOptionID]
            .split('__')
            .map(numberString => {
              return Number(numberString);
            });

        if (aCategory < bCategory) return -1;
        if (aCategory === bCategory && aOption < bOption) return -1;

        return 1;
      });
    },
    openDefaultCategories() {
      if (this.selectedFilters.length > 0) {
        eventBus.toggleCategories();
      }

      if (this.selectedFilters.length === 0) return;

      this.filtersByCategory.forEach(category => {
        const categoryOptionIDs = category.options.map(option => {
            return option.id;
          }),
          selectedOptions = this.selectedFilters.filter(selectedOptionID => {
            return categoryOptionIDs.indexOf(selectedOptionID) !== -1;
          });

        if (selectedOptions.length > 0) {
          document
            .querySelector(
              `.js-category-options-toggle[data-category="${category.id}"]`
            )
            .click();
        }
      });
    },
    sendAdobeToolEvent(eventStr) {
      const eventData = {
        type: 'tool',
        detail: {
          eVar35: eventStr,
        },
      };
      fireAdobeAnalyticsEvent(eventData);
    },
    sendAdobeDetailPageLoadEvent(project) {
      // Cleaned like GalleryProjectsController#analytics_project_name, so a
      // project opened here and one deep linked share a page name.
      const projectName = adobeCleanString(
        (project?.name || '').replace(/\|/g, ' ')
      );
      adobeAnalyticsVirtualPageLoad(`Project Gallery|Detail|${projectName}`);
    },
    sendAdobeSearchEvent() {
      clearTimeout(this.debounceTimeout);

      this.debounceTimeout = setTimeout(() => {
        // Gives 5 seconds before sending an empty result to Adobe
        const maxAttempts = 50;
        let attempts = 0;

        const tryFireEvent = () => {
          if (!this.loadingData || attempts >= maxAttempts) {
            // The term can be cleared while results load.
            const search = this.adobeSearchObject();
            if (!search) return;
            // Launch counts every tool event as a tool action (event35), so
            // a search needs its own eVar35 value.
            fireAdobeAnalyticsEvent({
              type: 'tool',
              detail: {
                eVar35: `Project Gallery|Search|${search.term}`,
                search,
              },
            });
          } else {
            attempts++;
            setTimeout(tryFireEvent, 100);
          }
        };

        tryFireEvent();
      }, 500);
    },
    adobeSearchObject() {
      if (!this.searchTerm) return null;

      return {
        term: this.searchTerm,
        results: !!this.results.length,
        toolName: 'Project Gallery',
      };
    },
  },
};
</script>
